'use client';

import type { ReactNode } from 'react';

import type { AbstractIntlMessages } from 'next-intl';
import { NextIntlClientProvider } from 'next-intl';

const isDevelopment = process.env.NODE_ENV === 'development';

interface I18nClientProviderProps {
  locale: string;
  messages: AbstractIntlMessages;
  children: ReactNode;
  timeZone?: string;
}

/**
 * Client-side provider for next-intl.
 * Wraps the application and provides translation context to all client components.
 */
export function I18nClientProvider({
  locale,
  messages,
  timeZone = 'UTC',
  children,
}: I18nClientProviderProps) {
  return (
    <NextIntlClientProvider
      locale={locale}
      messages={messages}
      timeZone={timeZone}
      getMessageFallback={(info) => {
        /*
         * The key, and that is the right answer here.
         *
         * Messages arrive already merged over the default locale (see
         * `mergeMessages` in the app's `i18n/request.ts`), so anything reaching
         * this point is missing from the default locale too — a message nobody
         * has written, rather than one nobody has translated. Naming the key
         * says which.
         */
        return info.key;
      }}
      onError={(error) => {
        if (isDevelopment) {
          // Missing translations are expected and should only log an error
          console.warn(`[Dev Only] i18n error: ${error.message}`);
        }
      }}
    >
      {children}
    </NextIntlClientProvider>
  );
}
